import { Component, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { SelectItem } from "primeng/api";
import { forkJoin, lastValueFrom, Observable } from "rxjs";
import { PeriforOnChangeMessages, SignalRService } from "src/app/shared/services/signal-r.service";
import { WellConfigService } from "src/app/shared/services/well-config.service";
import { WellheadModel } from "src/app/wellbore-inputs/components/wellhead/wellhead.model";
import { WellheadService } from "src/app/wellbore-inputs/services/wellhead.service";
import { StressResultsService } from "../../services/stress-results.service";
import { UserUnitsModel } from "src/app/core/components/user-units/user-units.model";
import { SessionStorageKeys, StorageKeys, StoreService } from "src/app/core/services/store.service";
import { LiftOffResultsUi } from "../../models/perivis-results-state.model";
import { Store } from "@ngneat/elf";
import { withUIEntities } from "@ngneat/elf-entities";
import { XyLinePlotUi } from "src/app/core/models/xy-line-plot.model";
import { HangerLiftoffForceResult } from "../../models/hangar-lift-off-force-result.model";
import { CurrentAppIdsDto } from "src/app/core/models/current-app-ids-dto";

@Component({
  selector: "app-liftoff-results",
  templateUrl: "./liftoff-results.component.html",
  styles: [],
  standalone: false
})
export class LiftoffResultsComponent implements OnInit, OnDestroy {

  public resultsDisplay: SelectItem[];
  public results: any[];
  public isLoading = signal<boolean>(true);
  public selectedResultDisplay: string = 'plot';
  public errorMsg: string | null = null;
  public configMissing: boolean;
  public wellheadDisabled = true;
  public hangerLiftoffForce: number;
  public userUnits: UserUnitsModel;
  public columnDefinitions: any[];
  public annotations: any[] = [];
  public shapes: any[] = [];
  public forceUnit: string;
  public lockdownRating: number;

  public xAxisTitle = '';
  public yAxisTitle = '';
  public plotName: string;
  public downloadPlotName = 'liftoff_results_plot';
  public plot = {
    data: [] as any[],
    layout: {
      yaxis: {
        title: {
          text: ''
        },
      }
    },
    config: {
      scrollZoom: false
    }
  };

  private selectedString: string;
  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.liftoffResultsStore = this._storeService.createStore(this.componentId, new LiftOffResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedResultDisplay = this.liftoffResultsStore.state.selectedResultDisplay || 'plot';
  }
  get componentId(): string {
    return this._componentId;
  }
  public liftoffResultsStore: Store;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _wellheadService: WellheadService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.forceUnit = this.userUnits.force;

    this.yAxisTitle = `Hanger Lift-Off Force (${this.forceUnit})`;

    this.checkConfig();

    this.resultsDisplay = [
      { label: 'Plot', value: 'plot' },
      { label: 'Grid', value: 'grid' }
    ];


    this.columnDefinitions = [
      {
        header: 'Load Case', field: 'loadCaseName',
        valueFormatter: (params: any) => params
      },
      {
        header: `Hanger Lift-Off Force (${this.forceUnit})`, field: 'hangerLiftoffForce',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];


    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, async d => this.signalRfunc(d));
    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    await this.refreshResults();
  }

  async signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_RESULTS || data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      await this.refreshResults();
    }
  }

  setPlotName(): string {
    return 'liftoffResultsPlot - ' + this.selectedString;
  }

  async onSelectedResultDisplayUpdated(e): Promise<void> {
    this.selectedResultDisplay = e.value;
    this.liftoffResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
    await this.refreshResults();
  }

  public async refreshResults(): Promise<void> {
    this.isLoading.set(true);
    this.errorMsg = null;

    try {
      const tubular = await lastValueFrom(this._wellConfigService.getTubular());

      if (!tubular) {
        this.isLoading.set(false);
        this.handleError("Well Configuration Missing: Please specify at least one string");
        return;
      }

      const sources = [
        this._wellheadService.getWellheadForTubular() as Observable<any>,
        this._stressResultsService.getLiftoffResults()
      ];
      const [wellhead, liftoffResults] = await lastValueFrom(forkJoin(sources));
      this.processWellheadData(wellhead, tubular, liftoffResults);
      this.plotName = this.setPlotName();
    } catch {
      this.handleError("Well Configuration Missing: Please specify at least one string");
    }
  }

  private validateLiftoffResults(liftoffResults: HangerLiftoffForceResult[]): boolean {
    this.isLoading.set(true);
    if (!liftoffResults.length || !liftoffResults[0].loadCaseName) {
      this.handleError("Please calculate PERIVIS to see results");
      this.isLoading.set(false);
      return false;
    }
    if (liftoffResults[0].loadCaseName === "Initial Condition" && liftoffResults[0].hangerLiftoffForce === 0) {
      this.handleError("Please make sure that Wellhead inputs are specified and recalculate PERIVIS");
      this.isLoading.set(false);
      return false;
    }
    return true;
  }

  private processWellheadData(
    wellhead: WellheadModel,
    tubular: any,
    liftoffResults: HangerLiftoffForceResult[]
  ): void {
    this.lockdownRating = +wellhead.hangerLockdownRating.toFixed(2);
    this.hangerLiftoffForce = -(+wellhead.hangerLockdownRating.toFixed(2));

    this.wellheadDisabled = tubular.sequenceNumber === 0 || tubular.type === 'Liner';

    if (!this.wellheadDisabled && !this.validateLiftoffResults(liftoffResults)) {
      this.results = liftoffResults;
      return;
    }

    this.errorMsg = this.wellheadDisabled ? "Not applicable for the outermost string or liners" : null;

    this.clearResults();

    if (!this.wellheadDisabled) {
      this.parseResults(liftoffResults);
      this.results = liftoffResults;
    }

    this.setHangerRating(wellhead, this.wellheadDisabled);
  }

  private handleError(message: string): void {
    this.errorMsg = message;
    this.isLoading.set(false);
    this.clearResults();
  }

  private clearResults(): void {
    this.results = [];
    this.plot.data = [];
  }

  private parseResults(results: any[]) {
    if (results === undefined) {
      return;
    }
    this.plot.config.scrollZoom = true;
    const traceArray = {
      x: [] as string[],
      y: [] as number[],
      type: "bar",
      marker: {
        color: [] as string[]
      },
      width: 0.55,
      showlegend: false
    };

    results.forEach((result) => {
      traceArray.x.push(result.loadCaseName);
      traceArray.y.push(result.hangerLiftoffForce);
      if (result.hangerLiftoffForce < this.hangerLiftoffForce) {
        traceArray.marker.color.push("rgb(239, 83, 80)");
      } else {
        traceArray.marker.color.push("rgb(43, 120, 182)");
      }
    });

    this.plot.data = [traceArray];
  }

  private setHangerRating(wellhead: WellheadModel, wellheadDisabled: boolean): void {
    if (!wellheadDisabled) {
      this.shapes = [
        {
          type: "line",
          xref: "paper",
          x0: 0,
          y0: -wellhead.hangerLockdownRating,
          x1: 1,
          y1: -wellhead.hangerLockdownRating,
          line: {
            color: "rgb(239, 83, 80)",
            width: 2,
            dash: "dot",
          },
        },
      ];

      this.annotations = [
        {
          showarrow: false,
          text: "Wellhead Hanger Lockdown Rating: " + wellhead.hangerLockdownRating.toFixed(2) + ` ${this.forceUnit}`,
          font: {
            color: "rgb(239, 83, 80)"
          },
          xref: "paper",
          align: "right",
          x: 1,
          xanchor: "right",
          y: -wellhead.hangerLockdownRating,
          yanchor: "bottom",
        },
      ];
    } else {
      this.shapes = [];
      this.annotations = [];
    }
    this.isLoading.set(false);
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0
      ? (Math.trunc(value * divisor) / divisor).toLocaleString("en-US")
      : null;
  }

  private checkConfig() {
    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this._wellConfigService.getStringsList(appIds?.designId).subscribe((data) => {
      this.configMissing = data.length < 1;
    });
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
